import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { Link } from "@components/Link/Link";
import { StyledButton } from "@components/Styled/Button";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import {
  ComponentProps,
  GatsbyCustomImage,
  ResolvedLink,
} from "@ts/components";
import { CustomImage } from "@bared/ui";
import { CustomImageType } from "@bared/groq";
import { ThemeAspectRatio } from "@ts/theme";
import React from "react";
import tw, { styled } from "twin.macro";
import { Image } from "./Image";

export type ImageCTAProps = ComponentProps & {
  alt: string;
  blog?: boolean;
  buttonLink?: string;
  buttonText: string;
  icon?: string;
  image?: GatsbyCustomImage;
  sanityImage?: CustomImageType;
  imageLink?: ResolvedLink;
  instagramHandle?: string;
  onClick?: () => void;
  onClickImage?: () => void;
  ratio?: "none" | Extract<keyof ThemeAspectRatio, string>;
};

const Wrapper = withFocusRing(tw.div`
  relative
`);

const Desktop = tw.div`
  absolute inset-0 hidden items-center justify-center opacity-0 group-hover:z-10 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-layout duration-slow md:flex
`;

const Mask = styledWithInvalidProps("div")(({ blog }) => [
  tw`absolute inset-0 opacity-70 bg-secondary`,
  blog ? tw`cursor-pointer` : null,
]);

const Button = styledWithInvalidProps(StyledButton)(({ blog }) => [
  blog ? tw`text-white` : tw`bg-white text-primary`,
  tw`z-10 w-full max-w-[232px]`,
]);

const StyledIcon = styled(Icon)`
  ${tw`absolute z-10 text-white top-mini right-mini md:top-midi md:right-midi`}
`;

const InstagramHandleStyled = tw.div`text-caption pt-mini break-all`;

const ImageCTA = ({
  image,
  buttonText,
  buttonLink,
  alt,
  onClick,
  blog,
  onClickImage,
  imageLink,
  ratio,
  icon,
  instagramHandle,
  sanityImage,
}: ImageCTAProps) => {
  return (
    <Wrapper className="group" tabIndex={0}>
      {buttonLink ? (
        <Desktop>
          {buttonLink && buttonText && (
            <Mask
              as={blog ? Link : null}
              to={buttonLink}
              blog={blog}
              tabIndex={-1}
              title={alt}
            />
          )}
          {buttonLink ? (
            <Button
              className="underlined-white"
              as={buttonLink ? Link : null}
              to={buttonLink}
              blog={blog}
              height="72-56"
              textSize="16"
            >
              <span>{buttonText}</span>
            </Button>
          ) : buttonText && onClick ? (
            <Button onClick={onClick} height="72-56" textSize="16">
              {buttonText}
            </Button>
          ) : null}
        </Desktop>
      ) : null}
      {icon?.length ? <StyledIcon name={icon} size="small" /> : null}
      {sanityImage ? (
        <CustomImage data={sanityImage} data-testid="customimage" />
      ) : (
        <Image
          alt={alt}
          image={image}
          ratio={ratio || `11/12`}
          onClick={onClickImage}
          imageLink={imageLink}
        />
      )}
      {instagramHandle?.length ? (
        <InstagramHandleStyled>@{instagramHandle}</InstagramHandleStyled>
      ) : null}
    </Wrapper>
  );
};

export default ImageCTA;
